import {dayToDateStr, type Day} from "../date-utils";
import type {FieldDesc, ModelDesc, ModelDescriptions, ModelName} from "./mate-types";

/**
 * Whether a row belongs in a filtered list. "unknown" means the client can't tell, and the list has
 * to be reloaded instead.
 */
export type Match = boolean | "unknown";

export type RelationKey = {
  variant: "list" | "count" | "exists";
  relName: string;
  query: Record<string, unknown> | null;
};

const relationKeyRegex = /^(count:|exists:)?([^(]+)(?:\((.*)\))?$/s;

/** Splits a cache key built by `processQuery`, e.g. `count:queueEntries({"cardDoneAt":null})`. */
export const parseRelationKey = (key: string): RelationKey | null => {
  const match = relationKeyRegex.exec(key);
  if (!match) return null;
  const [, prefix, relName, queryString] = match;
  const getVariant = () => {
    switch (prefix) {
      case "count:":
        return "count";
      case "exists:":
        return "exists";
      default:
        return "list";
    }
  };
  return {
    variant: getVariant(),
    relName,
    query: queryString ? (JSON.parse(queryString) as Record<string, unknown>) : null,
  };
};

const and = (results: Match[]): Match => {
  if (results.includes(false)) return false;
  return results.includes("unknown") ? "unknown" : true;
};

const or = (results: Match[]): Match => {
  if (results.includes(true)) return true;
  return results.includes("unknown") ? "unknown" : false;
};

type Comparable = string | number | bigint | boolean | null;

/** Brings a cached value and a query value to one form: the cache holds `Date`s and `Day`s, queries hold strings. */
const normalize = (type: FieldDesc["type"] | undefined, val: unknown): Comparable | undefined => {
  if (val === null) return null;
  switch (type) {
    case "date":
      if (val instanceof Date) return val.getTime();
      return typeof val === "string" || typeof val === "number"
        ? new Date(val).getTime()
        : undefined;
    case "day":
      if (typeof val === "string") return val;
      return typeof val === "object" ? dayToDateStr(val as Day) : undefined;
    case "bigint":
      return typeof val === "bigint" || typeof val === "number" || typeof val === "string"
        ? BigInt(val)
        : undefined;
    default:
      return typeof val === "object" ? undefined : (val as Comparable);
  }
};

/** Postgres orders strings by collation, which JS can't reproduce, so only these compare with lt/gt. */
const isOrderable = (type: FieldDesc["type"] | undefined, val: Comparable) =>
  typeof val === "number" || typeof val === "bigint" || (type === "day" && typeof val === "string");

// Mirrors `applyOperation` in node-api's graph-api/sql-query.ts, including SQL's null handling:
// `null != 'x'` and `null not in ('x')` are null, so a null row value matches neither.
const matchOperation = (
  type: FieldDesc["type"] | undefined,
  rowVal: unknown,
  filter: {op: string; value: unknown}
): Match => {
  const {op, value} = filter;
  switch (op) {
    case "eq":
    case "neq": {
      const left = normalize(type, rowVal);
      const right = normalize(type, value);
      if (left === undefined || right === undefined) return "unknown";
      if (right === null) return op === "eq" ? left === null : left !== null;
      if (left === null) return false;
      return op === "eq" ? left === right : left !== right;
    }
    case "lt":
    case "lte":
    case "gt":
    case "gte": {
      const left = normalize(type, rowVal);
      const right = normalize(type, value);
      if (left === undefined || right === undefined || right === null) return "unknown";
      if (left === null) return false;
      if (!isOrderable(type, left) || !isOrderable(type, right)) return "unknown";
      switch (op) {
        case "lt":
          return left < right;
        case "lte":
          return left <= right;
        case "gt":
          return left > right;
        case "gte":
        default:
          return left >= right;
      }
    }
    case "in":
    case "notIn":
    case "inOrNull": {
      if (!Array.isArray(value)) return "unknown";
      const left = normalize(type, rowVal);
      const rights = value.map((v) => normalize(type, v));
      if (left === undefined || rights.includes(undefined)) return "unknown";
      if (left === null) {
        const listHasNull = rights.includes(null);
        switch (op) {
          case "in":
            return listHasNull;
          case "notIn":
            return value.length === 0;
          case "inOrNull":
          default:
            return true;
        }
      }
      const isIn = rights.includes(left);
      return op === "notIn" ? !isIn : isIn;
    }
    case "has":
    case "notHas":
    case "overlaps": {
      if (rowVal === null) return false;
      if (!Array.isArray(rowVal)) return "unknown";
      if (op === "overlaps") {
        return Array.isArray(value) ? value.some((v) => rowVal.includes(v)) : "unknown";
      }
      const has = rowVal.includes(value);
      return op === "has" ? has : !has;
    }
    default:
      // contains (ilike), search, jsonSearch, cardUpvotes
      return "unknown";
  }
};

/** Turns the shorthands `processQuery` keeps (`null`, a value, an array) into the server's `{op, value}`. */
const toOperation = (val: unknown): {op: string; value: unknown} | null => {
  if (Array.isArray(val)) return {op: "in", value: val};
  if (val !== null && typeof val === "object") {
    return "op" in val ? (val as {op: string; value: unknown}) : null;
  }
  return {op: "eq", value: val};
};

/** What matching needs beyond the row itself. */
export type MatchContext = {
  descriptions: ModelDescriptions;
  /** a parent row, for a condition through a belongsTo relation; null when it isn't loaded */
  readRow: (model: ModelName, id: string) => Record<string, unknown> | null;
  /** a `var`'s value, as the server fills it in; undefined when it isn't loaded */
  readVar: (name: string) => string | null | undefined;
};

const not = (res: Match): Match => (res === "unknown" ? res : !res);

const matchConditions = (
  query: Record<string, unknown>,
  modelDesc: ModelDesc,
  row: Record<string, unknown>,
  context: MatchContext | undefined
): Match =>
  and(
    Object.entries(query).map(([key, val]): Match => {
      switch (key) {
        case "$and":
        case "$or": {
          // the server skips an empty `$and`/`$or`
          if (!Array.isArray(val) || val.length === 0) return true;
          const results = val.map((inner: Record<string, unknown>) =>
            matchConditions(inner, modelDesc, row, context)
          );
          return key === "$and" ? and(results) : or(results);
        }
        case "$order":
          return true;
        default:
          break;
      }
      // `$limit`, `$first`, `$offset` depend on the other rows
      if (key[0] === "$") return "unknown";

      const negated = key[0] === "!";
      const name = negated ? key.slice(1) : key;
      const parentRel = modelDesc.belongsTo[name];
      if (parentRel && val !== null && typeof val === "object" && !("op" in val)) {
        if (!context) return "unknown";
        const fkKey = parentRel.fk in row ? parentRel.fk : name;
        if (!(fkKey in row)) return "unknown";
        const parentId = row[fkKey];
        // the server's `exists (…)` finds nothing without a parent
        if (parentId === null) return negated;
        const parentRow = context.readRow(parentRel.model, parentId as string);
        if (!parentRow) return "unknown";
        const res = matchConditions(
          val as Record<string, unknown>,
          context.descriptions[parentRel.model],
          parentRow,
          context
        );
        return negated ? not(res) : res;
      }
      // `!rel` and `rel: {…}` through a has-many relation depend on rows of other lists
      if (negated) return "unknown";

      const fieldDesc = modelDesc.fields[key];
      const belongsTo = modelDesc.fkToBelongsTo[key];
      if (!fieldDesc && !belongsTo) return "unknown";

      // a parent's id is stored under the fk (`cardId`) or the relation's name (`card`), whichever
      // the query asked for
      const getRowKey = () => {
        if (key in row || !belongsTo) return key;
        const relName = Object.keys(modelDesc.belongsTo).find(
          (relKey) => modelDesc.belongsTo[relKey].fk === key
        );
        return relName ?? key;
      };
      const rowKey = getRowKey();
      if (!(rowKey in row)) return "unknown";

      if (val !== null && typeof val === "object" && "var" in val) {
        const varValue = context?.readVar((val as {var: string}).var);
        if (varValue === undefined) return "unknown";
        // the server matches no row for a request without the value
        if (varValue === null) return false;
        return matchOperation(fieldDesc?.type, row[rowKey], {op: "eq", value: varValue});
      }
      const operation = toOperation(val);
      if (!operation) return "unknown";
      return matchOperation(fieldDesc?.type, row[rowKey], operation);
    })
  );

/**
 * The fields of `modelDesc` a relation's `filter` reads: writing one can move a row into or out of
 * the relation. A condition through a parent (`{deck: {…}}`) reads the fk and the relation's name.
 */
export const filterFields = (query: Record<string, unknown>, modelDesc: ModelDesc): string[] =>
  Object.entries(query).flatMap(([key, val]) => {
    if (key === "$and" || key === "$or") {
      return (val as Record<string, unknown>[]).flatMap((inner) => filterFields(inner, modelDesc));
    }
    const name = key[0] === "!" ? key.slice(1) : key;
    // a filter on the fk (`{accountId: …}`) reads the parent as much as one through it
    const parentRel = modelDesc.belongsTo[name] ?? modelDesc.fkToBelongsTo[name];
    return parentRel ? [parentRel.fk, parentRel.name] : [name];
  });

/**
 * Checks `row` against a relation query. `row` holds the row's cached fields; a field it lacks makes
 * any condition on that field "unknown". Conditions through a belongsTo relation and `var`s need `context`.
 *
 * A model's `defaultScope` (`is_current`, `is_visible`, …) is raw SQL and is not checked here.
 */
export const rowMatchesQuery = (
  query: Record<string, unknown> | null,
  modelDesc: ModelDesc,
  row: Record<string, unknown>,
  context?: MatchContext
): Match => (query ? matchConditions(query, modelDesc, row, context) : true);
